import { useRef, useState } from 'react';
import { QrCode, Loader2, CheckCircle, RotateCcw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { aadhaarAPI } from '../../services/api';
import { decodeQrFromFile } from '../../utils/qrDecode';

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : (err?.message || fallback);
};

const DetailRow = ({ label, value, testId }) => (
  <div>
    <p className="text-gray-500 text-xs">{label}</p>
    <p data-testid={testId} className="font-medium text-gray-800 break-words">{value || '-'}</p>
  </div>
);

const formatAadhaar = (v) => v.replace(/(\d{4})(?=\d)/g, '$1 ');

const DecodedPreview = ({ data, aadhaar, setAadhaar, confirming, onConfirm, onReset }) => (
  <div className="space-y-4" data-testid="aadhaar-qr-preview">
    <div className="bg-[#F5E6C8]/40 rounded-lg p-4 border border-[#B8860B]/30 grid grid-cols-2 gap-4 text-sm">
      <DetailRow label="Name" value={data.name} testId="aadhaar-qr-name" />
      <DetailRow label="Aadhaar" value={`XXXX XXXX ${data.aadhaar_last_4}`} testId="aadhaar-qr-last4" />
      <DetailRow label="Date of Birth" value={data.date_of_birth} testId="aadhaar-qr-dob" />
      <DetailRow label="Gender" value={data.gender} testId="aadhaar-qr-gender" />
      <div className="col-span-2"><DetailRow label="Address" value={data.address} testId="aadhaar-qr-address" /></div>
    </div>
    <div>
      <label className="block text-sm font-medium text-[#004D40] mb-1.5">Full Aadhaar Number <span className="text-red-500">*</span></label>
      <input data-testid="aadhaar-qr-number-input" type="text" inputMode="numeric" maxLength={14}
        value={formatAadhaar(aadhaar)} onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
        placeholder={`XXXX XXXX ${data.aadhaar_last_4}`}
        className="w-full px-4 py-3 border border-[#B8860B]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40]/50 text-[#004D40] tracking-wider font-mono" />
      <p className="text-xs text-gray-500 mt-1">Must end with {data.aadhaar_last_4}. Used to make sure one Aadhaar links to only one account.</p>
    </div>
    <p className="text-xs text-gray-500">Confirm these details match your Aadhaar. Your name and address will be updated from Aadhaar.</p>
    <div className="flex gap-3">
      <button data-testid="aadhaar-qr-reset-btn" onClick={onReset} disabled={confirming}
        className="flex-1 py-3 border border-[#004D40]/40 text-[#004D40] rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#004D40]/5 transition-colors disabled:opacity-50">
        <RotateCcw className="w-4 h-4" /> Re-upload
      </button>
      <button data-testid="aadhaar-qr-confirm-btn" onClick={onConfirm} disabled={confirming || aadhaar.length !== 12}
        className="flex-1 py-3 bg-gradient-to-r from-[#004D40] to-[#00302B] text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50">
        {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
        {confirming ? 'Verifying...' : 'Confirm & Verify'}
      </button>
    </div>
  </div>
);

export const AadhaarQrUpload = ({ onVerified }) => {
  const inputRef = useRef(null);
  const [stage, setStage] = useState('idle');
  const [qrData, setQrData] = useState('');
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState('');
  const [aadhaar, setAadhaar] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload an image (JPG or PNG).'); return; }
    setError('');
    setStage('reading');
    try {
      const text = await decodeQrFromFile(file);
      const res = await aadhaarAPI.verifyQR(text);
      setQrData(text);
      setDecoded(res.data.data);
      setStage('preview');
    } catch (err) {
      setError(errorText(err, 'Could not read the Aadhaar QR code.'));
      setStage('idle');
    }
  };

  const confirm = async () => {
    if (!aadhaar.endsWith(decoded.aadhaar_last_4)) {
      toast.error('Aadhaar number does not match the uploaded QR code');
      return;
    }
    setStage('confirming');
    try {
      await aadhaarAPI.confirm(qrData, aadhaar);
      toast.success('Aadhaar verified successfully');
      onVerified?.();
    } catch (err) {
      toast.error(errorText(err, 'Verification failed'));
      setStage('preview');
    }
  };

  const reset = () => { setDecoded(null); setQrData(''); setAadhaar(''); setStage('idle'); };

  if (decoded) {
    return <DecodedPreview data={decoded} aadhaar={aadhaar} setAadhaar={setAadhaar} confirming={stage === 'confirming'} onConfirm={confirm} onReset={reset} />;
  }

  return (
    <div className="space-y-3" data-testid="aadhaar-qr-upload">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="aadhaar-qr-file-input" />
      <button data-testid="aadhaar-qr-upload-btn" onClick={() => inputRef.current?.click()} disabled={stage === 'reading'}
        className="w-full border-2 border-dashed border-[#B8860B]/50 rounded-xl p-6 flex flex-col items-center gap-2 text-[#004D40] hover:bg-[#F5E6C8]/30 transition-colors disabled:opacity-60">
        {stage === 'reading' ? <Loader2 className="w-8 h-8 animate-spin text-[#B8860B]" /> : <QrCode className="w-8 h-8 text-[#B8860B]" />}
        <span className="font-medium text-sm">{stage === 'reading' ? 'Reading QR code...' : 'Upload Aadhaar QR photo'}</span>
        <span className="text-xs text-gray-500 flex items-center gap-1"><Upload className="w-3 h-3" /> JPG / PNG of the QR on your Aadhaar card or e-Aadhaar</span>
      </button>
      {error && <p data-testid="aadhaar-qr-error" className="text-xs text-red-600">{error}</p>}
      <p className="text-xs text-gray-500">Tip: crop close to the QR code, with good light and no glare.</p>
    </div>
  );
};
